import { useMemo, FC } from 'react';

import { Box, Stack, Typography, TextField } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { visuallyHidden } from '@mui/utils';
import { DataGridPro } from '@mui/x-data-grid-pro';
import {
  GridColDef,
  GridEventListener,
  GridRenderCellParams,
} from '@mui/x-data-grid-pro';
import { format, addYears, subYears } from 'date-fns';
import { useTranslation } from 'react-i18next';

import styles from './AccountingPeriodDataGrid.module.scss';
import { getStatusDisplayValue } from './utils';
import {
  AccountingPeriod,
  AccountingPeriodStatus,
} from '@/api/REST/accountingPeriods';
import { CustomSelect, SelectOption } from '@/components/global/CustomSelect';
import { IconButton } from '@/components/ui/IconButton';
import { uiIcons } from '@/constants/uiIcons';

// Convert ISO date to YYYY-MM-DD format for HTML date input (timezone-safe)
const formatDateForInput = (dateString: string) => {
  if (!dateString) return '';
  // Extract just the date part (YYYY-MM-DD) without time/timezone conversion
  return dateString.split('T')[0];
};

interface AccountingPeriodDataGridProps {
  data: AccountingPeriod[];
  loading: boolean;
  onEdit: (period: AccountingPeriod) => void;
  onDelete: (period: AccountingPeriod) => void;
  onCellDoubleClick: GridEventListener<'cellDoubleClick'>;
  isAddingNew: boolean;
  newPeriod: Partial<AccountingPeriod>;
  onNewPeriodChange: (period: Partial<AccountingPeriod>) => void;
  onSaveNewPeriod: () => void;
  onCancelNewPeriod: () => void;
  newlyAddedPeriodId: string | null;
  mode: 'manual' | 'automatic';
  highlightedRowIds?: number[]; // New prop for highlighting specific rows
  // Edit existing period props
  editingPeriod: AccountingPeriod | null;
  onEditingPeriodChange: (period: Partial<AccountingPeriod>) => void;
  onSaveEditingPeriod: () => void;
  onCancelEditingPeriod: () => void;
  isLoadingEditingPeriod?: boolean;
}

export const AccountingPeriodDataGrid: FC<AccountingPeriodDataGridProps> = ({
  data,
  loading,
  onEdit,
  onDelete,
  onCellDoubleClick,
  isAddingNew,
  newPeriod,
  onNewPeriodChange,
  onSaveNewPeriod,
  onCancelNewPeriod,
  newlyAddedPeriodId,
  mode,
  highlightedRowIds = [],
  editingPeriod,
  onEditingPeriodChange,
  onSaveEditingPeriod,
  onCancelEditingPeriod,
}) => {
  const { t } = useTranslation('systemAdministrationNavigation');

  // Date restrictions: 10 years back to 5 years forward from today
  const dateRestrictions = useMemo(() => {
    const today = new Date();
    const minDate = subYears(today, 10); // 10 years back from today
    const maxDate = addYears(today, 5); // 5 years forward from today

    return {
      min: format(minDate, 'yyyy-MM-dd'),
      max: format(maxDate, 'yyyy-MM-dd'),
    };
  }, []);

  // Color mapping for status badges
  const colors = {
    pink: '#e84895',
    darkPink: '#c75dab',
    lightPink: '#f191bf',
    softPink: '#f5e1ea',
    fog: '#6c757d',
    gray: '#d2d2d2',
    darkGray: '#686f78',
    purple: '#6f42c1',
    yellowLight: '#fff3cd',
  };

  const getStatusStyles = (status: AccountingPeriodStatus) => {
    switch (status) {
      case AccountingPeriodStatus.Open:
        return {
          color: colors.purple,
          backgroundColor: alpha(colors.purple, 0.2),
        };
      case AccountingPeriodStatus.ClosedForRegistration:
        return {
          color: colors.purple,
          backgroundColor: colors.yellowLight,
        };
      case AccountingPeriodStatus.ClosedForRegistrationAndInvoicing:
        return {
          color: colors.purple,
          backgroundColor: alpha(colors.pink, 0.2),
        };
      case AccountingPeriodStatus.Closed:
      default:
        return {
          color: colors.purple,
          backgroundColor: alpha(colors.fog, 0.2),
        };
    }
  };

  // Status options for new period
  const statusOptions: SelectOption[] = [
    {
      value: AccountingPeriodStatus.Open,
      label: t('SystemAdministrationAccountingPeriodStatusOpen'),
    },
    {
      value: AccountingPeriodStatus.ClosedForRegistration,
      label: t(
        'SystemAdministrationAccountingPeriodStatusClosedForRegistration',
      ),
    },
    {
      value: AccountingPeriodStatus.ClosedForRegistrationAndInvoicing,
      label: t(
        'SystemAdministrationAccountingPeriodStatusClosedForRegistrationAndInvoicing',
      ),
    },
    {
      value: AccountingPeriodStatus.Closed,
      label: t('SystemAdministrationAccountingPeriodStatusClosed'),
    },
  ];

  // Transform API data to grid format
  const transformedData = useMemo(() => {
    const baseData = data.map((period) => {
      // Check if this period is being edited
      const isBeingEdited =
        editingPeriod &&
        editingPeriod.accountingPeriodId === period.accountingPeriodId;

      return {
        id: period.accountingPeriodId.toString(),
        accountingPeriodId: period.accountingPeriodId,
        startDate: isBeingEdited
          ? editingPeriod?.startDate || period.startDate
          : period.startDate,
        endDate: isBeingEdited
          ? editingPeriod?.endDate || period.endDate
          : period.endDate,
        status: isBeingEdited
          ? editingPeriod?.status || period.status
          : period.status,
        createdBy: period.createdBy,
        lastModifiedBy: period.lastModifiedBy,
        created: period.created,
        lastModified: period.lastModified,
        accountingPeriodGuid: period.accountingPeriodGuid,
        accountingPeriodDates: period.accountingPeriodDates,
        isEditing: isBeingEdited,
        isNewPeriod: false,
      };
    });

    // If we're adding a new period, add it at the top
    if (isAddingNew) {
      const newRow = {
        id: 'new-period-editing',
        accountingPeriodId: 0,
        startDate: newPeriod.startDate || '',
        endDate: newPeriod.endDate || '',
        status: newPeriod.status || AccountingPeriodStatus.Open,
        createdBy: 0,
        lastModifiedBy: 0,
        created: null,
        lastModified: '',
        accountingPeriodGuid: '',
        accountingPeriodDates: [],
        isEditing: true,
        isNewPeriod: true,
      };
      return [newRow, ...baseData];
    }

    return baseData;
  }, [data, isAddingNew, newPeriod, editingPeriod]);

  // Base columns that are always shown
  const baseColumns: GridColDef[] = [
    {
      field: 'status',
      type: 'string',
      width: 220,
      headerName: t('SystemAdministrationAccountingPeriodStatus'),
      headerAlign: 'left',
      align: 'left',
      renderCell: ({ row }: GridRenderCellParams) => {
        // If this is the new period being edited, show disabled dropdown
        if (row.isEditing && row.isNewPeriod) {
          return (
            <CustomSelect
              value={newPeriod.status || AccountingPeriodStatus.Open}
              onChange={(value) =>
                onNewPeriodChange({
                  ...newPeriod,
                  status: value as AccountingPeriodStatus,
                })
              }
              options={statusOptions}
              maxWidth={200}
              disabled={true}
            />
          );
        }

        // If this is an existing period being edited, show enabled dropdown
        if (row.isEditing && !row.isNewPeriod) {
          return (
            <CustomSelect
              value={editingPeriod?.status || row.status}
              onChange={(value) => {
                if (editingPeriod) {
                  onEditingPeriodChange({
                    ...editingPeriod,
                    status: value as AccountingPeriodStatus,
                  });
                }
              }}
              options={statusOptions}
              maxWidth={200}
              disabled={false}
            />
          );
        }

        // Default: show status badge (not editable)
        const statusStyles = getStatusStyles(row.status);
        return (
          <Box className={styles.StatusChip} sx={statusStyles}>
            {getStatusDisplayValue(row.status)}
          </Box>
        );
      },
      sortable: false,
    },
    {
      field: 'startDate',
      flex: 1,
      minWidth: 150,
      headerName: t('SystemAdministrationAccountingPeriodStartDate'),
      display: 'flex',
      cellClassName: 'textEllipsis',
      renderCell: ({ row }: GridRenderCellParams) => {
        // If this is the new period being edited, show date input
        if (row.isEditing && row.isNewPeriod) {
          return (
            <TextField
              type="date"
              value={newPeriod.startDate || ''}
              onChange={(e) => {
                onNewPeriodChange({ ...newPeriod, startDate: e.target.value });
              }}
              size="small"
              variant="outlined"
              placeholder={t('SystemAdministrationAccountingPeriodRequired')}
              className={styles.DateInput}
              inputProps={{
                min: dateRestrictions.min,
                max: dateRestrictions.max,
              }}
            />
          );
        }

        // If this is an existing period being edited, show date input
        if (row.isEditing && !row.isNewPeriod) {
          const startDateValue = formatDateForInput(
            editingPeriod?.startDate || row.startDate,
          );

          return (
            <TextField
              type="date"
              value={startDateValue}
              onChange={(e) => {
                if (editingPeriod) {
                  onEditingPeriodChange({
                    ...editingPeriod,
                    startDate: e.target.value,
                  });
                }
              }}
              size="small"
              variant="outlined"
              placeholder={t('SystemAdministrationAccountingPeriodRequired')}
              className={styles.DateInput}
              inputProps={{
                min: dateRestrictions.min,
                max: dateRestrictions.max,
              }}
            />
          );
        }

        return (
          <Typography fontWeight={400} sx={{ userSelect: 'none' }}>
            {new Date(row.startDate).toLocaleDateString()}
          </Typography>
        );
      },
      sortable: false,
    },
    {
      field: 'endDate',
      flex: 1,
      minWidth: 150,
      headerName: t('SystemAdministrationAccountingPeriodEndDate'),
      display: 'flex',
      cellClassName: 'textEllipsis',
      renderCell: ({ row }: GridRenderCellParams) => {
        // If this is the new period being edited, show date input
        if (row.isEditing && row.isNewPeriod) {
          return (
            <TextField
              type="date"
              value={newPeriod.endDate || ''}
              onChange={(e) => {
                onNewPeriodChange({ ...newPeriod, endDate: e.target.value });
              }}
              size="small"
              variant="outlined"
              placeholder={t('SystemAdministrationAccountingPeriodRequired')}
              className={styles.DateInput}
              inputProps={{
                min: dateRestrictions.min,
                max: dateRestrictions.max,
              }}
            />
          );
        }

        // If this is an existing period being edited, show date input
        if (row.isEditing && !row.isNewPeriod) {
          const endDateValue = formatDateForInput(
            editingPeriod?.endDate || row.endDate,
          );

          return (
            <TextField
              type="date"
              value={endDateValue}
              onChange={(e) => {
                if (editingPeriod) {
                  onEditingPeriodChange({
                    ...editingPeriod,
                    endDate: e.target.value,
                  });
                }
              }}
              size="small"
              variant="outlined"
              placeholder={t('SystemAdministrationAccountingPeriodRequired')}
              className={styles.DateInput}
              inputProps={{
                min: dateRestrictions.min,
                max: dateRestrictions.max,
              }}
            />
          );
        }

        return (
          <Typography fontWeight={400} sx={{ userSelect: 'none' }}>
            {new Date(row.endDate).toLocaleDateString()}
          </Typography>
        );
      },
      sortable: false,
    },
  ];

  // Actions column - only shown in manual mode
  const actionsColumn: GridColDef = {
    field: 'actions',
    width: 100,
    renderHeader: () => (
      <Box sx={visuallyHidden}>
        {t('SystemAdministrationAccountingPeriodActions')}
      </Box>
    ),
    align: 'center',
    display: 'flex',
    renderCell: ({ row }: GridRenderCellParams) => {
      // If this is the new period being edited, show save and cancel buttons
      if (row.isEditing && row.isNewPeriod) {
        return (
          <Stack direction="row">
            <IconButton
              tooltip={t('SystemAdministrationAccountingPeriodSaveNewPeriod')}
              onClick={onSaveNewPeriod}
              data-automation-id="AccountingPeriodSaveButton"
              iconName={uiIcons.status.confirm}
              size="small"
            />
            <IconButton
              tooltip={t('SystemAdministrationAccountingPeriodCancel')}
              onClick={onCancelNewPeriod}
              data-automation-id="AccountingPeriodCancelButton"
              iconName={uiIcons.actions.close}
              size="small"
            />
          </Stack>
        );
      }

      // If this is an existing period being edited, show update and cancel buttons
      if (row.isEditing && !row.isNewPeriod) {
        return (
          <Stack direction="row">
            <IconButton
              tooltip={t('SystemAdministrationAccountingPeriodUpdatePeriod')}
              onClick={onSaveEditingPeriod}
              data-automation-id="AccountingPeriodUpdateButton"
              iconName={uiIcons.status.confirm}
              size="small"
            />
            <IconButton
              tooltip={t('SystemAdministrationAccountingPeriodCancel')}
              onClick={onCancelEditingPeriod}
              data-automation-id="AccountingPeriodCancelEditButton"
              iconName={uiIcons.actions.close}
              size="small"
            />
          </Stack>
        );
      }

      // Check if the accounting period is editable (Open, ClosedForRegistration, or ClosedForRegistrationAndInvoicing)
      const isActive =
        row.status === AccountingPeriodStatus.Open ||
        row.status === AccountingPeriodStatus.ClosedForRegistration ||
        row.status === AccountingPeriodStatus.ClosedForRegistrationAndInvoicing;

      // Handle edit button
      const handleEdit = () => {
        if (isActive) {
          onEdit(row);
        }
      };

      // Handle delete button
      const handleDelete = () => {
        if (isActive) {
          onDelete(row);
        }
      };

      return (
        <Stack direction="row">
          <IconButton
            tooltip={
              isActive
                ? `${t('SystemAdministrationAccountingPeriodEdit')} ${row.accountingPeriodId}`
                : `${t('SystemAdministrationAccountingPeriodEdit')} ${row.accountingPeriodId} (${t('SystemAdministrationAccountingPeriodDisabled')} - ${t('SystemAdministrationAccountingPeriodPeriodIs')} ${getStatusDisplayValue(row.status)})`
            }
            onClick={handleEdit}
            disabled={!isActive}
            data-automation-id={`AccountingPeriodEditButton${row.accountingPeriodId}`}
            className={styles.EditButton}
            iconName={uiIcons.actions.edit}
            size="small"
          />
          <IconButton
            tooltip={
              isActive
                ? `${t('SystemAdministrationAccountingPeriodDelete')} ${row.accountingPeriodId}`
                : `${t('SystemAdministrationAccountingPeriodDelete')} ${row.accountingPeriodId} (${t('SystemAdministrationAccountingPeriodDisabled')} - ${t('SystemAdministrationAccountingPeriodPeriodIs')} ${getStatusDisplayValue(row.status)})`
            }
            onClick={handleDelete}
            disabled={!isActive}
            data-automation-id={`AccountingPeriodDeleteButton${row.accountingPeriodId}`}
            className={styles.DeleteButton}
            iconName={uiIcons.actions.delete}
            size="small"
          />
        </Stack>
      );
    },
    sortable: false,
  };

  // Combine columns based on mode
  const columns: GridColDef[] =
    mode === 'manual' ? [...baseColumns, actionsColumn] : baseColumns;

  return (
    <div className={styles.DataGridContainer}>
      <DataGridPro
        columns={columns}
        rows={transformedData}
        loading={loading}
        pagination
        pageSizeOptions={[20, 50, 100]}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 20,
            },
          },
        }}
        onCellDoubleClick={onCellDoubleClick}
        getRowClassName={(params) => {
          if (params.id === newlyAddedPeriodId) {
            return styles.NewlyAddedRow;
          }
          if (highlightedRowIds.includes(Number(params.id))) {
            return styles.HighlightedRow;
          }
          return '';
        }}
        className={styles.DataGridOverrides}
      />
    </div>
  );
};
